import { useEffect, useMemo, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useMountEffect from '../../hooks/useMountEffect';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { CHART_METRIC, VIEW_MODE } from './LifeCycleAnalyticsConstants';
import {
	getDefaultFilters,
	filtersToParams,
	filtersToQueryParams,
	queryParamsToFilters,
	parseSafeInt,
	clampDateRange,
} from './LifeCycleAnalyticsUtils';
import queryString from 'qs';
import Color from 'color';

const BACK_LINK_TEXT = 'Back to Life Cycle Analytics';

/**
 * URL sync, server state, priority/metric navigation, and filter draft handlers
 * for Lifecycle Analytics overview.
 */
export function useLifeCycleAnalyticsData(props: any) {
	const {
		companyConfig,
		currentUser,
		userType,
		problemTypes,
		workOrderPriorities,
		fetchPrioritySummary,
		fetchSupplierPerformance,
		fetchMonthlyApprovalTime,
		fetchMonthlyResponseTime,
		fetchMonthlyResolutionTime,
		fetchMonthlyFirstTimeFixRate,
		fetchMonthlySpend,
		fetchProblemTypes,
		fetchWorkOrderPriorities,
		updateNavbar,
		clearNavbar,
	} = props;

	const history = useHistory();
	const location = useLocation();

	const { initialFilters, initialPriorityId, initialMinWoCount } = useMemo(() => {
		const search = location.search;
		const str = search && search[0] === '?' ? search.slice(1) : search;
		const qp = queryString.parse(str || '');
		const hasParams = Object.keys(qp).length > 0;
		return {
			initialFilters: hasParams ? queryParamsToFilters(qp) : getDefaultFilters(),
			initialPriorityId: qp.priorityId ? parseSafeInt(qp.priorityId, null) : null,
			initialMinWoCount: qp.minWoCount ? parseSafeInt(qp.minWoCount, 5) : 5,
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const [loading, setLoading] = useState(true);
	const [chartLoading, setChartLoading] = useState(true);
	const [prioritiesLoading, setPrioritiesLoading] = useState(true);
	const [error, setError] = useState('');
	const [prioritySummary, setPrioritySummary] = useState([]);
	const [supplierPerformance, setSupplierPerformance] = useState([]);
	const [allMonthlyMetricData, setAllMonthlyMetricData] = useState<Record<string, any[]>>({});
	const [monthlySpend, setMonthlySpend] = useState([]);

	const [activeFilters, setActiveFilters] = useState(initialFilters);
	const [draftFilters, setDraftFilters] = useState(initialFilters);
	const [filterModalVisible, setFilterModalVisible] = useState(false);
	const [selectedPriorityId, setSelectedPriorityId] = useState(initialPriorityId);
	const [minWoCount, setMinWoCount] = useState(initialMinWoCount);
	const [viewMode, setViewMode] = useState(VIEW_MODE.ALL);
	const [createdAtPickerDates, setCreatedAtPickerDates] = useState(null);
	const [completedAtPickerDates, setCompletedAtPickerDates] = useState(null);

	useEffect(() => {
		const params = filtersToQueryParams(activeFilters, selectedPriorityId, minWoCount);
		const search = queryString.stringify(params);
		history.replace(location.pathname + (search ? '?' + search : ''));
	}, [activeFilters, selectedPriorityId, minWoCount]); // eslint-disable-line react-hooks/exhaustive-deps

	const overviewPath = useMemo(() => `/${userType}/analytics/lifecycle/overview`, [userType]);

	const navbarBackgroundColor = useMemo(() => {
		const navColor = nullSafeGetOrElse('detail.config.theme.navblock', companyConfig, '#6237a0');
		return Color(navColor).darken(0.2).toString();
	}, [companyConfig]);

	useEffect(() => {
		if (!updateNavbar || !clearNavbar) return;
		if (viewMode === VIEW_MODE.ALL) {
			clearNavbar();
			return;
		}
		updateNavbar(BACK_LINK_TEXT, overviewPath, navbarBackgroundColor);
	}, [viewMode, overviewPath, navbarBackgroundColor, updateNavbar, clearNavbar]);

	useMountEffect(() => {
		return () => {
			if (clearNavbar) clearNavbar();
		};
	});

	useEffect(() => {
		const search = location.search;
		const str = search && search[0] === '?' ? search.slice(1) : search;
		const qp = queryString.parse(str || '');
		const urlPriorityId = qp.priorityId ? parseSafeInt(qp.priorityId, null) : null;
		if (urlPriorityId === null && selectedPriorityId !== null) {
			setSelectedPriorityId(null);
			setViewMode(VIEW_MODE.ALL);
		}
	}, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

	const isAutoApprovalEnabled = useMemo(() => {
		const approvalConfig = nullSafeGetOrElse('detail.config.approvalConfig', companyConfig, {});
		return (
			nullSafeGetOrElse('autoApproveServiceRequests', approvalConfig, false) &&
			nullSafeGetOrElse('autoApproveSupplierInitiatedWorkOrders', approvalConfig, false)
		);
	}, [companyConfig]);

	const loadChartData = useCallback(
		(filters, priorityId) => {
			setChartLoading(true);
			const params = filtersToParams(filters);
			if (priorityId !== null) {
				params.woPriorityId = priorityId;
			}
			const chartRequests = [
				fetchSupplierPerformance({}, params),
				...(isAutoApprovalEnabled ? [] : [fetchMonthlyApprovalTime({}, params)]),
				fetchMonthlyResponseTime({}, params),
				fetchMonthlyResolutionTime({}, params),
				fetchMonthlyFirstTimeFixRate({}, params),
				fetchMonthlySpend({}, params),
			];
			Promise.all(chartRequests)
				.then((result) => {
					const [supplierData, ...metricResults] = result;
					const [approvalData, responseData, resolutionData, ftfrData, spendData] =
						isAutoApprovalEnabled ? [null, ...metricResults] : metricResults;
					setSupplierPerformance(supplierData || []);
					setAllMonthlyMetricData({
						...(isAutoApprovalEnabled ? {} : { [CHART_METRIC.APPROVAL_TIME]: approvalData || [] }),
						[CHART_METRIC.RESPONSE_TIME]: responseData || [],
						[CHART_METRIC.RESOLUTION_TIME]: resolutionData || [],
						[CHART_METRIC.FIRST_TIME_FIX_RATE]: ftfrData || [],
					});
					setMonthlySpend(spendData || []);
				})
				.catch((e) => {
					setError((e && e.message) || String(e) || 'Failed to load analytics data.');
				})
				.finally(() => {
					setChartLoading(false);
				});
		},
		[
			fetchSupplierPerformance,
			fetchMonthlyApprovalTime,
			fetchMonthlyResponseTime,
			fetchMonthlyResolutionTime,
			fetchMonthlyFirstTimeFixRate,
			fetchMonthlySpend,
			isAutoApprovalEnabled,
		]
	);

	const loadAllData = useCallback(
		(filters, priorityIdHint) => {
			setLoading(true);
			setChartLoading(true);
			setError('');
			const params = filtersToParams(filters);
			fetchPrioritySummary({}, params)
				.then((priorityData) => {
					const data = priorityData || [];
					setPrioritySummary(data);
					const sorted = data.slice().sort((a, b) => a.woPriorityId - b.woPriorityId);
					const hintIsValid =
						priorityIdHint !== null &&
						priorityIdHint !== undefined &&
						sorted.some((row) => row.woPriorityId === priorityIdHint);

					setViewMode(VIEW_MODE.ALL);
					setSelectedPriorityId(null);
					setSupplierPerformance([]);
					setAllMonthlyMetricData({});
					setMonthlySpend([]);

					if (!hintIsValid) {
						return null;
					}
					setSelectedPriorityId(priorityIdHint);
					setViewMode(VIEW_MODE.SINGLE);
					const chartParams = filtersToParams(filters);
					chartParams.woPriorityId = priorityIdHint;
					return Promise.all([
						fetchSupplierPerformance({}, chartParams),
						...(isAutoApprovalEnabled ? [] : [fetchMonthlyApprovalTime({}, chartParams)]),
						fetchMonthlyResponseTime({}, chartParams),
						fetchMonthlyResolutionTime({}, chartParams),
						fetchMonthlyFirstTimeFixRate({}, chartParams),
						fetchMonthlySpend({}, chartParams).catch(() => []),
					]);
				})
				.then((chartResult) => {
					if (!chartResult) {
						return;
					}
					const [supplierData, ...metricResults] = chartResult;
					const [approvalData, responseData, resolutionData, ftfrData, monthlySpendData] =
						isAutoApprovalEnabled ? [null, ...metricResults] : metricResults;
					setSupplierPerformance(supplierData || []);
					setAllMonthlyMetricData({
						...(isAutoApprovalEnabled ? {} : { [CHART_METRIC.APPROVAL_TIME]: approvalData || [] }),
						[CHART_METRIC.RESPONSE_TIME]: responseData || [],
						[CHART_METRIC.RESOLUTION_TIME]: resolutionData || [],
						[CHART_METRIC.FIRST_TIME_FIX_RATE]: ftfrData || [],
					});
					setMonthlySpend(monthlySpendData || []);
				})
				.catch((e) => {
					setError((e && e.message) || String(e) || 'Failed to load Lifecycle Analytics data.');
				})
				.finally(() => {
					setLoading(false);
					setChartLoading(false);
				});
		},
		[
			fetchPrioritySummary,
			fetchSupplierPerformance,
			fetchMonthlyApprovalTime,
			fetchMonthlyResponseTime,
			fetchMonthlyResolutionTime,
			fetchMonthlyFirstTimeFixRate,
			fetchMonthlySpend,
			isAutoApprovalEnabled,
		]
	);

	useEffect(() => {
		Promise.resolve(fetchWorkOrderPriorities())
			.catch((e) => {
				console.error('Failed to load work order priorities', e);
			})
			.finally(() => {
				setPrioritiesLoading(false);
			});
		loadAllData(initialFilters, initialPriorityId);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const priorityMap = useMemo(() => {
		return nullSafeGetOrElse('records', workOrderPriorities, {});
	}, [workOrderPriorities]);

	const openFilterModal = () => {
		setDraftFilters({ ...activeFilters });
		setFilterModalVisible(true);
	};

	const applyFilters = () => {
		setActiveFilters(draftFilters);
		setFilterModalVisible(false);
		loadAllData(draftFilters, selectedPriorityId);
	};

	const cancelFilters = () => {
		setFilterModalVisible(false);
	};

	const removeProblemTypeFilter = () => {
		const newFilters = { ...activeFilters, problemTypeIds: [] };
		setActiveFilters(newFilters);
		loadAllData(newFilters, selectedPriorityId);
	};

	const removeDateFilter = useCallback(
		(field) => {
			const newFilters = { ...activeFilters, [field]: null };
			setActiveFilters(newFilters);
			loadAllData(newFilters, selectedPriorityId);
		},
		[activeFilters, selectedPriorityId, loadAllData]
	);

	const handleArrowClick = useCallback(
		(priorityId) => {
			if (viewMode === VIEW_MODE.ALL) {
				setSelectedPriorityId(priorityId);
				setViewMode(VIEW_MODE.SINGLE);
				loadChartData(activeFilters, priorityId);
				return;
			}

			if (selectedPriorityId === priorityId) return;
			setSelectedPriorityId(priorityId);
			setViewMode(VIEW_MODE.SINGLE);
			loadChartData(activeFilters, priorityId);
		},
		[viewMode, selectedPriorityId, activeFilters, loadChartData]
	);

	const handleCardClick = useCallback(
		(priorityId) => {
			if (selectedPriorityId === priorityId) return;
			setSelectedPriorityId(priorityId);
			setViewMode(VIEW_MODE.SINGLE);
			loadChartData(activeFilters, priorityId);
		},
		[selectedPriorityId, activeFilters, loadChartData]
	);

	const navigateToSupplierDetail = useCallback(
		(supplierFacilityId) => {
			if (!supplierFacilityId) return;
			const filterParams = filtersToQueryParams(
				activeFilters,
				selectedPriorityId,
				minWoCount,
				viewMode
			);
			const pathName = '/' + userType + '/analytics/lifecycle/overview';
			const backlinkParts = {
				pathName: pathName,
				backLinkText: 'Back to Lifecycle Analytics',
				...filterParams,
			};
			const backlinkParams = encodeURIComponent(queryString.stringify(backlinkParts));
			history.push(
				'/' +
					userType +
					'/analytics/kpi/vendors/detail/' +
					supplierFacilityId +
					'?backlinkParams=' +
					backlinkParams
			);
		},
		[activeFilters, selectedPriorityId, minWoCount, viewMode, userType, history]
	);

	const handleRemoveCreatedAtFilter = useCallback(
		() => removeDateFilter('createdAt'),
		[removeDateFilter]
	);
	const handleRemoveCompletedAtFilter = useCallback(
		() => removeDateFilter('completedAt'),
		[removeDateFilter]
	);

	const handleProblemTypeChange = useCallback(
		(ids) => setDraftFilters((prev) => ({ ...prev, problemTypeIds: ids || [] })),
		[]
	);
	const handleCreatedAtCalendarChange = useCallback((dates) => setCreatedAtPickerDates(dates), []);
	const handleCreatedAtChange = useCallback((dates) => {
		setDraftFilters((prev) => ({ ...prev, createdAt: clampDateRange(dates) }));
		setCreatedAtPickerDates(null);
	}, []);
	const handleCreatedAtOpenChange = useCallback((open) => {
		if (!open) setCreatedAtPickerDates(null);
	}, []);

	const handleCompletedAtCalendarChange = useCallback(
		(dates) => setCompletedAtPickerDates(dates),
		[]
	);
	const handleCompletedAtChange = useCallback((dates) => {
		setDraftFilters((prev) => ({ ...prev, completedAt: clampDateRange(dates) }));
		setCompletedAtPickerDates(null);
	}, []);
	const handleCompletedAtOpenChange = useCallback((open) => {
		if (!open) setCompletedAtPickerDates(null);
	}, []);

	const handleIsPMChange = useCallback(
		(value) => setDraftFilters((prev) => ({ ...prev, isPM: value })),
		[]
	);
	const handleWorkHoursTypeChange = useCallback(
		(value) => setDraftFilters((prev) => ({ ...prev, workHoursType: value })),
		[]
	);

	return {
		companyConfig,
		currentUser,
		userType,
		problemTypes,
		fetchProblemTypes,
		history,
		loading,
		chartLoading,
		prioritiesLoading,
		error,
		prioritySummary,
		supplierPerformance,
		allMonthlyMetricData,
		monthlySpend,
		activeFilters,
		draftFilters,
		filterModalVisible,
		selectedPriorityId,
		minWoCount,
		setMinWoCount,
		viewMode,
		createdAtPickerDates,
		completedAtPickerDates,
		isAutoApprovalEnabled,
		priorityMap,
		handleArrowClick,
		handleCardClick,
		navigateToSupplierDetail,
		openFilterModal,
		applyFilters,
		cancelFilters,
		removeProblemTypeFilter,
		handleRemoveCreatedAtFilter,
		handleRemoveCompletedAtFilter,
		handleProblemTypeChange,
		handleCreatedAtCalendarChange,
		handleCreatedAtChange,
		handleCreatedAtOpenChange,
		handleCompletedAtCalendarChange,
		handleCompletedAtChange,
		handleCompletedAtOpenChange,
		handleIsPMChange,
		handleWorkHoursTypeChange,
	};
}
